import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, ERROR_CODES } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { ApplicationNames } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as get from 'lodash/get';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { EngagementsDataEffectUtils } from '../../services/engagements/engagements-data-effect-utils.service';
import { EngagementsDataService } from '../../services/engagements/engagements-data.service';
import { EngagementsHelperService } from '../../services/engagements/engagements-helper.service';
import { EngagementsUtil } from '../../utils/engagements.util';
import * as EngagementsAPIActions from './engagements-api.actions';
import * as EngagementsUIActions from './engagements-ui.actions';

@Injectable()
export class EngagementsEffects {
	constructor(
		private engagementsDataService: EngagementsDataService,
		private engagementsDataEffectUtils: EngagementsDataEffectUtils,
		private engagementsHelperService: EngagementsHelperService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private actions$: Actions) { }

	public loadAllEngagements$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.loadEngagementsRequested
			),
			mergeMap(() =>
				this.engagementsDataEffectUtils.handleLoadEngagements()
			)
		)
	);

	public loadOneEngagement$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.loadOneEngagementRequested
			),
			mergeMap((action) =>
				this.engagementsDataEffectUtils.handleLoadOneUserEngagement(action.engagementId, ERROR_CODES.PAC_ERROR_CODES.LOAD_ONE_ERROR)
			)
		)
	);

	public updateEngagements$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.updateEngagementRequested
			),
			mergeMap((action) =>
				this.engagementsDataEffectUtils.handleUpdateAndAddEngagements(action.mode, action.engagement, action.errorCode, action.updateAnalysis)
			)
		)
	);

	public updateEngagementsSucceeded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsAPIActions.updateEngagementsSucceeded
			),
			map((action) => {
				if (action.updateAnalysis) {
					this.engagementsDataEffectUtils.triggerAnalysisUpdate(action.engagements[0]);
				}
				return EngagementsUIActions.engagementsNoop();
			})
		)
	);

	public updateEngagementForPortfolioEdit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.updateEngagementAsyncRequested
			),
			mergeMap((action) =>
				this.engagementsDataEffectUtils.handleUpdateForPortfolioEdit(
					action.mode,
					action.engagement,
					ERROR_CODES.PAC_ERROR_CODES.UPDATE_EDIT_PORTFOLIO_ORDER_ERROR,
					action.updateAnalysis
				)
			)
		)
	);

	public closeActiveEngagementsAndStartNewEngagement$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.closeActiveEngagementsAndStartNewEngagementRequested
			),
			mergeMap((action) => {
				return this.engagementsDataService.saveMultipleExistingEngagements(action.engagements)
					.pipe(
						map(engagements => {
							return EngagementsAPIActions.closeEngagementsSucceeded({ engagements });
						}),
						catchError(error =>
							this.engagementsDataEffectUtils.handleError(
								error,
								EngagementsAPIActions.updateEngagementsFailed({ errorCode: ERROR_CODES.PAC_ERROR_CODES.UPDATE_CLOSE_CURRENT_ENGAGEMENT_ERROR })
							)
						)
					);
			})
		)
	);

	public closeEngagementsSucceeded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsAPIActions.closeEngagementsSucceeded
			),
			mergeMap(() => {
				return this.engagementsDataService.addNewEngagement(
					EngagementsUtil.createNewEngagement(this.engagementsHelperService.getDefaultBpasEngagementCurrency()))
					.pipe(
						map(engagements => {
							return EngagementsAPIActions.openNewEngagementSucceeded({ engagement: get(engagements, '[0]', '') });
						}),
						catchError(error =>
							this.engagementsDataEffectUtils.handleError(
								error,
								EngagementsAPIActions.updateEngagementsFailed({ errorCode: ERROR_CODES.PAC_ERROR_CODES.UPDATE_OPEN_NEW_ENGAGEMENT })
							)
						)
					);
			})
		)
	);

	public addEngagement$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.addEngagementRequested
			),
			mergeMap((action) =>
				this.engagementsDataEffectUtils.handleUpdateAndAddEngagements(action.mode, action.engagement, ERROR_CODES.PAC_ERROR_CODES.ADD_ERROR, false)
			)
		)
	);

	public removePortfolioFromEngagement$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsUIActions.removePortfolioFromEngagementRequested
			),
			mergeMap((action) =>
				this.engagementsDataEffectUtils.handleRemovePortfolioFromEngagement(action.portfolioId, action.engagement, ERROR_CODES.PAC_ERROR_CODES.DELETE_PORTFOLIO_ERROR)
			)
		)
	);

	// showAlertOnFailure
	public showAlertOnFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				EngagementsAPIActions.loadEngagementsFailed,
				EngagementsAPIActions.updateEngagementsFailed,
				EngagementsAPIActions.removePortfolioFromEngagementFailed
			),
			tap((action) => {
				throw new ErrorObject({ errorCode: action.errorCode }, ERROR_CODES.PAC_ERROR_CODES.ENGAGEMENT_CONFIG_NAME, ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION);
			})
		)
	);
}
